import { ExternalLink, ImageIcon, Pencil, Upload, X } from 'lucide-react'
import * as React from 'react'

import { toast } from '@/hooks/use-toast'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import uploadService from '@/services/upload.service'

import { ImageEditorDialog } from './puck-image-editor'

// ============================================
// TYPES
// ============================================

interface ImagePickerFieldProps {
  value: unknown
  onChange: (value: unknown) => void
}

// ============================================
// IMAGE PICKER FIELD COMPONENT
// ============================================

/**
 * Enhanced image picker with:
 * - URL input
 * - File upload (drag & drop)
 * - Image preview
 * - Edit/Crop button to open image editor dialog
 */
export function ImagePickerField({ value, onChange }: ImagePickerFieldProps) {
  const imageUrl = typeof value === 'string' ? value : ''
  const [previewError, setPreviewError] = React.useState(false)
  const [activeTab, setActiveTab] = React.useState<string>('url')
  const [isUploading, setIsUploading] = React.useState(false)
  const [isDragging, setIsDragging] = React.useState(false)
  const [editorOpen, setEditorOpen] = React.useState(false)

  React.useEffect(() => {
    setPreviewError(false)
  }, [imageUrl])

  // ============================================
  // FILE UPLOAD HANDLER
  // ============================================

  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file type', description: 'Please upload an image file', variant: 'destructive' })
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum file size is 10MB', variant: 'destructive' })
      return
    }

    setIsUploading(true)
    try {
      const result = await uploadService.uploadImage({ image: file })
      onChange(result.url)
      toast({ title: 'Image uploaded', description: 'Image uploaded successfully' })
    } catch {
      toast({ title: 'Upload failed', description: 'Failed to upload image', variant: 'destructive' })
    } finally {
      setIsUploading(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFileUpload(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => setIsDragging(false)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFileUpload(file)
  }

  const handleCropComplete = (newUrl: string) => {
    onChange(newUrl)
  }

  // ============================================
  // RENDER
  // ============================================

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
      {/* Tabs for different input methods */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-2">
          <TabsTrigger value="url">URL</TabsTrigger>
          <TabsTrigger value="upload">Upload</TabsTrigger>
        </TabsList>

        {/* URL Tab */}
        <TabsContent value="url" className="mt-2">
          <input
            type="text"
            value={imageUrl}
            onChange={e => onChange(e.target.value)}
            placeholder="Enter image URL..."
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </TabsContent>

        {/* Upload Tab */}
        <TabsContent value="upload" className="mt-2">
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            style={{
              border: `2px dashed ${isDragging ? '#3b82f6' : '#d1d5db'}`,
              borderRadius: '8px',
              padding: '24px',
              textAlign: 'center',
              backgroundColor: isDragging ? '#eff6ff' : '#f9fafb',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="image-upload-input"
              disabled={isUploading}
            />
            <label htmlFor="image-upload-input" style={{ cursor: 'pointer' }}>
              <Upload style={{ width: '32px', height: '32px', margin: '0 auto 8px', color: '#6b7280' }} />
              <p style={{ fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
                {isUploading ? 'Uploading...' : 'Drop image or click to upload'}
              </p>
              <p style={{ fontSize: '12px', color: '#6b7280' }}>PNG, JPG, WEBP up to 10MB</p>
            </label>
          </div>
        </TabsContent>
      </Tabs>

      {/* Preview Section */}
      {imageUrl && !previewError && (
        <div
          style={{
            position: 'relative',
            width: '100%',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            backgroundColor: '#f3f4f6',
          }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={imageUrl}
            alt="Preview"
            onError={() => setPreviewError(true)}
            style={{
              width: '100%',
              maxHeight: '200px',
              objectFit: 'cover',
              display: 'block',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              display: 'flex',
              gap: '4px',
            }}
          >
            {/* Edit / Crop button */}
            <button
              type="button"
              onClick={() => setEditorOpen(true)}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: 'rgba(59,130,246,0.8)',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Edit / Crop image"
            >
              <Pencil style={{ width: '14px', height: '14px' }} />
            </button>
            <button
              type="button"
              onClick={() => window.open(imageUrl, '_blank')}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Open in new tab"
            >
              <ExternalLink style={{ width: '14px', height: '14px' }} />
            </button>
            <button
              type="button"
              onClick={() => onChange('')}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Remove image"
            >
              <X style={{ width: '14px', height: '14px' }} />
            </button>
          </div>
        </div>
      )}

      {/* No Image Placeholder */}
      {!imageUrl && (
        <div
          style={{
            width: '100%',
            height: '100px',
            borderRadius: '8px',
            border: '1px dashed #d1d5db',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6b7280',
            backgroundColor: '#f9fafb',
          }}
        >
          <ImageIcon style={{ width: '24px', height: '24px', marginBottom: '4px' }} />
          <span style={{ fontSize: '12px' }}>No image selected</span>
        </div>
      )}

      {/* Image Editor Dialog */}
      {imageUrl && (
        <ImageEditorDialog
          open={editorOpen}
          onOpenChange={setEditorOpen}
          imageSrc={imageUrl}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  )
}

// Keep backward compatibility alias
export const ImageFieldRender = ImagePickerField
